/**
 * CreateTemplatePage - Simple form to create a new template
 * 
 * Route: /dashboard/templates/new
 * 
 * Only asks for name and type, then redirects to the appropriate editor.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MessageSquare, Mail, Code, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { createTemplate, NotificationChannel } from '../../utils/notificationsBroadcastApi';

export const CreateTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialChannel = searchParams.get('channel') as NotificationChannel | null;

  const [name, setName] = useState('');
  const [channel, setChannel] = useState<NotificationChannel>(initialChannel || 'email_html');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const template = await createTemplate({
        name: name.trim(),
        channel,
        subject: channel !== 'sms' ? '' : undefined,
        body: ' ', // Placeholder - will be edited after creation
      });

      // Navigate to the appropriate editor
      if (channel === 'email_html') {
        // HTML templates go directly to visual builder
        navigate(`/dashboard/templates/${template.id}?mode=visual`);
      } else {
        // SMS/Plain go to text editor
        navigate(`/dashboard/templates/${template.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create template');
      setCreating(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/templates');
  };

  return (
    <div className="h-full flex flex-col items-center justify-center bg-gray-50 dark:bg-dark-bg-primary p-6">
      <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-dark-border">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-primary mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Templates
          </button>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary">
            Create New Template
          </h1>
          <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
            Choose a name and type for your template
          </p>
        </div>

        {/* Form */}
        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Template Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Template Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) {
                  handleCreate();
                }
              }}
              className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Appointment Reminder, Welcome Email"
              autoFocus
            />
          </div>

          {/* Template Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Template Type
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setChannel('sms')}
                className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl transition-all ${
                  channel === 'sms'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 shadow-sm'
                    : 'border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary text-gray-600 dark:text-dark-text-primary hover:border-gray-300'
                }`}
              >
                <MessageSquare className="w-6 h-6" />
                <span className="text-sm font-medium">SMS</span>
              </button>
              <button
                type="button"
                onClick={() => setChannel('email_plain')}
                className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl transition-all ${
                  channel === 'email_plain'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 shadow-sm'
                    : 'border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary text-gray-600 dark:text-dark-text-primary hover:border-gray-300'
                }`}
              >
                <Mail className="w-6 h-6" />
                <span className="text-sm font-medium">Plain Email</span>
              </button>
              <button
                type="button"
                onClick={() => setChannel('email_html')}
                className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl transition-all ${
                  channel === 'email_html'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 shadow-sm'
                    : 'border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary text-gray-600 dark:text-dark-text-primary hover:border-gray-300'
                }`}
              >
                <Code className="w-6 h-6" />
                <span className="text-sm font-medium">HTML Email</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border flex justify-end gap-3 bg-gray-50 dark:bg-dark-bg-tertiary">
          <button
            onClick={handleCancel}
            disabled={creating}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-hover rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || !name.trim()}
            className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create & Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplatePage;
